import { TableBody } from "@/components/ui/table";
import { useVirtualRowStyle } from "@/components/useVirtualRowStyles";
import { cn } from "@/lib/utils";
import { Table } from "@tanstack/react-table";
import { memo, useCallback, useMemo, useRef } from "react";
import useBusy from "../hooks/useBusy";
import useRowVirtualizer from "../hooks/useRowVirtualizer";
import DataTableRow from "./DataTableRow";

interface DataTableBodyProps<TData extends { id: string }> {
  table: Table<TData>;
  rowClassName?: string;
  getRowProps?: <T>(row: { original: T }) => Record<string, unknown>;
  onRowMouseEnter?: (rowId: string) => void;
  onRowMouseLeave?: (rowId: string) => void;
  scrollContainerElementRef: React.ForwardedRef<HTMLDivElement>;
  isInDialog?: boolean;
}

export const MemoizedDataTableBody = memo(
  DataTableBody,
  (prev, next) => prev.table.options.data === next.table.options.data,
) as typeof DataTableBody;

export function DataTableBody<TData extends { id: string }>({
  table,
  rowClassName,
  getRowProps,
  onRowMouseEnter,
  onRowMouseLeave,
  scrollContainerElementRef,
  isInDialog,
}: DataTableBodyProps<TData>) {
  const rows = table.getRowModel().rows;
  const tableBodyRef = useRef(null);

  const rowVirtualizer = useRowVirtualizer(
    rows.length,
    tableBodyRef,
    scrollContainerElementRef,
    isInDialog,
  );
  const virtualRows = rowVirtualizer.getVirtualItems();
  const isBusy = useBusy();
  const totalSize = rowVirtualizer.getTotalSize();
  const onRowMouseEnterInternal = useCallback(
    (rowId: string) => {
      onRowMouseEnter?.(rowId);
    },
    [onRowMouseEnter],
  );

  const onRowMouseLeaveInternal = useCallback(
    (rowId: string) => {
      onRowMouseLeave?.(rowId);
    },
    [onRowMouseLeave],
  );

  useVirtualRowStyle(rowVirtualizer, tableBodyRef);

  const style = useMemo(() => {
    return {
      height: `${totalSize}px`, // tells scrollbar how big the table is
    };
  }, [totalSize]);

  return (
    <TableBody
      className={cn("grid relative overflow-visible bg-white", {
        "opacity-50 pointer-events-none blur-[2px]": isBusy,
      })}
      style={style}
      ref={tableBodyRef}
    >
      {virtualRows.map((virtualRow) => {
        const row = rows[virtualRow.index];
        return (
          <DataTableRow
            key={row.id}
            row={row}
            rowClassName={rowClassName}
            getRowProps={getRowProps}
            virtualRow={virtualRow}
            onRowMouseEnterInternal={onRowMouseEnterInternal}
            onRowMouseLeaveInternal={onRowMouseLeaveInternal}
          />
        );
      })}
    </TableBody>
  );
}
